import React, {
  useCallback,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { Stack, Paper, MenuItem, Button } from "@mui/material";
import { useEditor, EditorContent, ReactRenderer } from "@tiptap/react";
import tippy from "tippy.js";

import { getCompactExtensions } from "../HubEditor/extensions";
import { EditorToolbar } from "../HubEditor";
import { StyledEditorChrome, StyledToolbar } from "../HubEditor/styles";
import { HubButton } from "../inputs";

const MentionList = forwardRef(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);

  const selectItem = (index) => {
    const item = items[index];
    if (item) command({ id: item.id, label: item.name });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelected((s) => (s + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelected((s) => (s + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        selectItem(selected);
        return true;
      }
      return false;
    },
  }));

  return (
    <Paper elevation={4} sx={{ maxHeight: 200, overflow: "auto" }}>
      {items.length ? (
        items.map((item, i) => (
          <MenuItem
            key={item.id}
            selected={i === selected}
            onClick={() => selectItem(i)}
            sx={{ fontSize: 12, py: 0.5 }}
          >
            {item.name}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled sx={{ fontSize: 12 }}>
          No users found
        </MenuItem>
      )}
    </Paper>
  );
});

const getSuggestion = (users) => ({
  items: ({ query }) =>
    users
      .filter((u) => u.name?.toLowerCase().includes(query.toLowerCase()))
      .slice(0, 8),
  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(MentionList, {
          props,
          editor: props.editor,
        });
        popup = tippy("body", {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props) => {
        component?.updateProps(props);
        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
      },
      onKeyDown: (props) => component?.ref?.onKeyDown(props),
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});

const CommentEditor = ({
  users = [],
  onSubmit,
  onCancel,
  initialContent,
  placeholder = "Write a comment...",
}) => {
  const mentionSuggestion = users.length ? getSuggestion(users) : undefined;
  const extensions = getCompactExtensions({ placeholder, mentionSuggestion });

  const editor = useEditor({
    extensions,
    content: initialContent || "",
  });

  useEffect(() => {
    if (initialContent && editor) {
      editor.commands.setContent(initialContent);
    }
  }, [initialContent]);

  const isEmpty = !editor?.state.doc.textContent.trim();

  const handleSubmit = useCallback(() => {
    if (!editor || isEmpty) return;
    const json = editor.getJSON();
    onSubmit(json);
    editor.commands.clearContent();
  }, [editor, isEmpty, onSubmit]);

  return (
    <Stack gap="6px">
      <StyledEditorChrome>
        <StyledToolbar>
          <EditorToolbar editor={editor} compact />
        </StyledToolbar>
        <EditorContent
          editor={editor}
          style={{ minHeight: 60, padding: "8px 12px", fontSize: 12 }}
        />
      </StyledEditorChrome>
      <Stack
        direction="row"
        justifyContent="flex-end"
        gap="6px"
        marginTop="-42px"
        paddingRight="10px"
      >
        {onCancel && (
          <HubButton size="xsmall" label="Cancel" onClick={onCancel} />
        )}
        <HubButton
          size="xsmall"
          variant="contained"
          label="Comment"
          disabled={isEmpty}
          onClick={handleSubmit}
        />
      </Stack>
    </Stack>
  );
};

export default CommentEditor;
